import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Save, RotateCcw, Eye, Type, Bold, Italic, Move } from 'lucide-react';
import { Rnd } from 'react-rnd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 10 familias tipográficas disponibles (Banner WhatsApp también las usa)
const FONT_FAMILIES = [
  { id: '',                label: '(default — Barlow/Anton)' },
  { id: 'inter',           label: 'Inter (UI moderno)' },
  { id: 'sans',            label: 'Sans (Arial)' },
  { id: 'sans-condensed',  label: 'Sans Condensed (Arial Narrow)' },
  { id: 'narrow-bold',     label: 'Narrow Bold (impacto)' },
  { id: 'serif',           label: 'Serif (Times)' },
  { id: 'mono',            label: 'Mono (Courier)' },
  { id: 'freesans',        label: 'Free Sans' },
  { id: 'freeserif',       label: 'Free Serif (clásica)' },
  { id: 'freemono',        label: 'Free Mono' },
  { id: 'loma',            label: 'Loma (humanista)' },
];

// Mapeo id-backend → font-family CSS (para que el preview HTML se vea ~igual al PNG)
const cssFontFamily = (id, fallback) => {
  const map = {
    'inter': 'Inter, system-ui, sans-serif',
    'sans': 'Arial, Helvetica, sans-serif',
    'sans-condensed': '"Arial Narrow", "Helvetica Narrow", Arial, sans-serif',
    'narrow-bold': '"Arial Narrow", "Helvetica Narrow", Arial Black, sans-serif',
    'serif': '"Times New Roman", Times, serif',
    'mono': '"Courier New", Courier, monospace',
    'freesans': 'Arial, sans-serif',
    'freeserif': '"Times New Roman", serif',
    'freemono': '"Courier New", monospace',
    'loma': 'Verdana, sans-serif',
  };
  return map[id] || fallback;
};

const DEFAULTS = {
  logo: { top: 1.5, left: 4, width: 32, rotate: 0 },
  image: { top: 1, right: 1, width: 48, height: 60, rotate: 0 },
  text: { top: 30, left: 5, width: 56, maxHeight: 60 },
  // NUEVO: bloques separados (si están definidos, pisan a "text")
  textName: null,   // { top, left, width, maxHeight } en %
  textPrice: null,  // idem
  textQty: null,    // idem
  namePx: 28, pricePx: 60, lineHeight: 1.05,
  nameLines: 3, showFooter: true, footerText: '📲 Pedilo por WhatsApp',
  bgColor: '#ffffff', nameColor: '#EC4899', priceColor: '#06B6D4', accentColor: '#FB923C',
  footerBgColor: '#ffffff', footerTextColor: '#404040',
  showDiscountBadge: true, showOfertaBadge: true, showDestacadoBadge: true, showTierBadge: true,
  topText: '', topTextColor: '#EF4444', topTextPx: 24,
  borderWidth: 0, borderColor: '#EC4899',
  showCatalogUrl: false, catalogUrlText: 'bebida-bacana.com', catalogUrlPx: 12,
  // NUEVO: Precios por objetivo de carrito (tier list) — visible en el banner
  showTierList: true,
  tierListPx: 18,
  tierListColor: '#0F172A',
  tierListAccentColor: '#EC4899',
  // Fondo OPACO por defecto para evitar que la sub-línea de precio o los
  // badges se transparenten a través del bloque. Editable desde el admin.
  tierListBgColor: '#FFFFFF',
  tierListBold: true,
  tierListTitle: 'Precios por objetivo',
  tierList: { left: 4, bottom: 18, width: 92 },
  // Tipografía
  fontFamily: '', nameBold: true, nameItalic: false, nameUppercase: true,
  priceBold: true, priceItalic: false,
  topBold: true, topItalic: false,
  footerBold: true, footerItalic: false,
  // Stickers
  stickers: [],
  // Precios por forma de pago — sólo aplica para banners de productos sin
  // descuento por cantidad y para clientes COMERCIO (regla del cliente).
  paymentChipsEnabled: true,
  paymentChipBg: 'rgba(255,255,255,0.94)',
  paymentChipPx: 22,
  paymentChipPadX: 10, paymentChipPadY: 4, paymentChipRadius: 999,
  paymentChipBorderPx: 2,
  paymentChipDiscountColor: '#047857',
  paymentChipDiscountBorder: '#10B981',
  paymentChipSurchargeColor: '#9F1239',
  paymentChipSurchargeBorder: '#F43F5E',
  paymentChipBold: true,
  paymentChipGap: 6,
};

export default function AdminBannerLayoutPage() {
  const [layout, setLayout] = useState(DEFAULTS);
  const [storeSettings, setStoreSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [previewId, setPreviewId] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [editMode, setEditMode] = useState(true);     // NUEVO: drag/resize habilitado
  const [previewW, setPreviewW] = useState(540);       // ancho real del preview en pixels
  const [selectedFmt, setSelectedFmt] = useState('ig-story');  // ig-story exclusivo (9:16 Instagram Story / WhatsApp Status)
  const [previewZoom, setPreviewZoom] = useState(100);  // 50-200%, escala visual del editor
  const previewRef = useRef(null);
  const autoSaveRef = useRef(null);
  const initialLoadRef = useRef(true);

  // Autosave debounced (2s) — para que las descargas siempre tengan los cambios
  useEffect(() => {
    if (initialLoadRef.current) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => { save(); }, 2000);
    return () => clearTimeout(autoSaveRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/store-settings`),
      axios.get(`${API}/admin/pricing/products`, { params: { limit: 50, active: true } }),
    ]).then(([s, p]) => {
      setStoreSettings(s.data);
      const persistedLayout = s.data.bannerLayout || {};
      setLayout({ ...DEFAULTS, ...persistedLayout,
        format: 'ig-story',
        logo: { ...DEFAULTS.logo, ...(persistedLayout.logo || {}) },
        image: { ...DEFAULTS.image, ...(persistedLayout.image || {}) },
        text: { ...DEFAULTS.text, ...(persistedLayout.text || {}) },
      });
      // Formato FIJO: 9:16 (ig-story). Ignoramos el `persistedLayout.format`
      // aunque venga con 'square' u otro; ya no soportamos otros formatos.
      setSelectedFmt('ig-story');
      const items = p.data.items || [];
      setProducts(items);
      setPreviewId(items[0]?.id || '');
      setTimeout(() => { initialLoadRef.current = false; }, 100);
    });
  }, []);

  const product = useMemo(() => products.find(p => p.id === previewId), [products, previewId]);

  // Medir el ancho real del preview para que <Rnd> trabaje en pixels reales
  useEffect(() => {
    const measure = () => {
      if (previewRef.current) {
        setPreviewW(previewRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [product, selectedFmt]);

  const upd = (path, val) => {
    setLayout(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // PUT del settings completo: el backend acepta solo el StoreSettings full
      const payload = { ...storeSettings, bannerLayout: layout };
      delete payload.type;
      await axios.put(`${API}/admin/store-settings`, payload);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 1800);
    } catch (e) {
      // Silenciar errores en autosave para no molestar al usuario
      // eslint-disable-next-line no-console
      console.warn('autosave error', e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const resetDefaults = () => {
    if (window.confirm('Restaurar valores por defecto?')) setLayout(DEFAULTS);
  };

  const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-AR')}`;

  return (
    <div className="p-3 sm:p-5 max-w-[1500px] mx-auto" data-testid="admin-banner-layout-page">
      <header className="mb-3 flex items-center gap-2">
        <ImageIcon size={22} className="text-zinc-700" />
        <h1 className="text-xl sm:text-2xl font-black text-zinc-900">Editor de Banner WhatsApp</h1>
        <span className="text-xs text-zinc-500 hidden md:inline ml-2">— Configurá la posición de logo, imagen y descripción para evitar superposiciones.</span>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Controles */}
        <div className="space-y-3">
          <Card title="Logo (esquina superior)">
            <Row label="Top (%)" v={layout.logo.top} onChange={v => upd('logo.top', v)} min={0} max={50} step={0.5} />
            <Row label="Left (%)" v={layout.logo.left} onChange={v => upd('logo.left', v)} min={-10} max={70} step={0.5} />
            <Row label="Ancho (%)" v={layout.logo.width} onChange={v => upd('logo.width', v)} min={5} max={90} step={1} />
            <Row label="Rotación (°)" v={layout.logo.rotate || 0} onChange={v => upd('logo.rotate', v)} min={-180} max={180} step={5} />
          </Card>

          <Card title="Imagen del producto">
            <Row label="Top (%)" v={layout.image.top} onChange={v => upd('image.top', v)} min={-30} max={90} step={0.5} />
            <Row label="Right (%)" v={layout.image.right} onChange={v => upd('image.right', v)} min={-30} max={80} step={0.5} />
            <Row label="Ancho (%)" v={layout.image.width} onChange={v => upd('image.width', v)} min={10} max={140} step={1} />
            <Row label="Alto (%)" v={layout.image.height} onChange={v => upd('image.height', v)} min={10} max={160} step={1} />
            <Row label="Rotación (°)" v={layout.image.rotate || 0} onChange={v => upd('image.rotate', v)} min={-180} max={180} step={5} />
          </Card>

          <Card title="Bloque: NOMBRE del producto">
            <Row label="Top (%)" v={(layout.textName || layout.text).top} onChange={v => upd('textName', { ...(layout.textName || layout.text), top: v })} min={0} max={90} step={0.5} />
            <Row label="Left (%)" v={(layout.textName || layout.text).left} onChange={v => upd('textName', { ...(layout.textName || layout.text), left: v })} min={0} max={80} step={0.5} />
            <Row label="Ancho (%)" v={(layout.textName || layout.text).width} onChange={v => upd('textName', { ...(layout.textName || layout.text), width: v })} min={20} max={100} step={1} />
            <Row label="Alto máx. (%)" v={(layout.textName || layout.text).maxHeight} onChange={v => upd('textName', { ...(layout.textName || layout.text), maxHeight: v })} min={5} max={80} step={1} />
            <Row label="Tamaño nombre (px)" v={layout.namePx} onChange={v => upd('namePx', v)} min={14} max={72} step={1} />
            <Row label="Líneas máx." v={layout.nameLines} onChange={v => upd('nameLines', v)} min={1} max={8} step={1} />
            <Row label="Interlínea" v={layout.lineHeight} onChange={v => upd('lineHeight', v)} min={0.85} max={1.6} step={0.05} />
          </Card>

          <Card title="Bloque: PRECIO">
            <Row label="Top (%)" v={(layout.textPrice || { top: 55 }).top} onChange={v => upd('textPrice', { ...(layout.textPrice || { left: 5, width: 60, maxHeight: 18, top: 55 }), top: v })} min={0} max={95} step={0.5} />
            <Row label="Left (%)" v={(layout.textPrice || { left: 5 }).left} onChange={v => upd('textPrice', { ...(layout.textPrice || { top: 55, width: 60, maxHeight: 18, left: 5 }), left: v })} min={0} max={80} step={0.5} />
            <Row label="Ancho (%)" v={(layout.textPrice || { width: 60 }).width} onChange={v => upd('textPrice', { ...(layout.textPrice || { top: 55, left: 5, maxHeight: 18, width: 60 }), width: v })} min={20} max={100} step={1} />
            <Row label="Tamaño precio (px)" v={layout.pricePx} onChange={v => upd('pricePx', v)} min={20} max={140} step={1} />
          </Card>

          <Card title="Bloque: CANTIDAD / BADGES">
            <Row label="Top (%)" v={(layout.textQty || { top: 80 }).top} onChange={v => upd('textQty', { ...(layout.textQty || { left: 5, width: 60, maxHeight: 15, top: 80 }), top: v })} min={0} max={95} step={0.5} />
            <Row label="Left (%)" v={(layout.textQty || { left: 5 }).left} onChange={v => upd('textQty', { ...(layout.textQty || { top: 80, width: 60, maxHeight: 15, left: 5 }), left: v })} min={0} max={80} step={0.5} />
            <Row label="Ancho (%)" v={(layout.textQty || { width: 60 }).width} onChange={v => upd('textQty', { ...(layout.textQty || { top: 80, left: 5, maxHeight: 15, width: 60 }), width: v })} min={20} max={100} step={1} />
          </Card>

          <Card title="Colores">
            <ColorRow label="Fondo" v={layout.bgColor} onChange={(v) => upd('bgColor', v)} testid="bl-bg" />
            <ColorRow label="Nombre" v={layout.nameColor} onChange={(v) => upd('nameColor', v)} testid="bl-namec" />
            <ColorRow label="Precio" v={layout.priceColor} onChange={(v) => upd('priceColor', v)} testid="bl-pricec" />
            <ColorRow label="Acento (badges)" v={layout.accentColor} onChange={(v) => upd('accentColor', v)} testid="bl-accent" />
          </Card>

          <Card title="Tipografía (servidor)">
            <p className="text-[10px] text-zinc-500">Aplica al PNG que se manda por WhatsApp. El preview HTML de la derecha usa las fuentes web (Barlow/Anton).</p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-700 w-32 shrink-0 flex items-center gap-1"><Type size={12} /> Familia</label>
              <select value={layout.fontFamily || ''} onChange={(e) => upd('fontFamily', e.target.value)}
                className="flex-1 px-2 py-1 border border-zinc-200 rounded text-xs bg-white"
                data-testid="bl-font">
                {FONT_FAMILIES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <StyleRow label="Nombre" b={layout.nameBold} i={layout.nameItalic}
              onBold={(v) => upd('nameBold', v)} onItalic={(v) => upd('nameItalic', v)} testid="bl-name-style" />
            <Check label="Nombre en MAYÚSCULAS" v={layout.nameUppercase} onChange={(v) => upd('nameUppercase', v)} testid="bl-name-upper" />
            <StyleRow label="Precio" b={layout.priceBold} i={layout.priceItalic}
              onBold={(v) => upd('priceBold', v)} onItalic={(v) => upd('priceItalic', v)} testid="bl-price-style" />
            <StyleRow label="Texto superior" b={layout.topBold} i={layout.topItalic}
              onBold={(v) => upd('topBold', v)} onItalic={(v) => upd('topItalic', v)} testid="bl-top-style" />
            <StyleRow label="Pie de página" b={layout.footerBold} i={layout.footerItalic}
              onBold={(v) => upd('footerBold', v)} onItalic={(v) => upd('footerItalic', v)} testid="bl-footer-style" />
          </Card>

          <Card title="Texto promocional superior">
            <input value={layout.topText} onChange={(e) => upd('topText', e.target.value)}
              className="w-full px-2 py-1.5 border border-zinc-200 rounded text-sm"
              placeholder='Ej: "OFERTA DE LA SEMANA"' data-testid="bl-toptext" />
            {layout.topText && (
              <>
                <ColorRow label="Color" v={layout.topTextColor} onChange={(v) => upd('topTextColor', v)} testid="bl-topcolor" />
                <Row label="Tamaño (px)" v={layout.topTextPx} onChange={(v) => upd('topTextPx', v)} min={14} max={48} step={1} />
              </>
            )}
          </Card>

          <Card title="Badges (chips)">
            <Check label="Mostrar % de descuento" v={layout.showDiscountBadge} onChange={(v) => upd('showDiscountBadge', v)} testid="bl-disc" />
            <Check label="Mostrar OFERTA 🔥" v={layout.showOfertaBadge} onChange={(v) => upd('showOfertaBadge', v)} testid="bl-ofer" />
            <Check label="Mostrar DESTACADO ⭐" v={layout.showDestacadoBadge} onChange={(v) => upd('showDestacadoBadge', v)} testid="bl-dest" />
            <Check label="Mostrar precio por cantidad" v={layout.showTierBadge} onChange={(v) => upd('showTierBadge', v)} testid="bl-tier" />
          </Card>

          <Card title="🌟 Stickers de venta (rayos, fuego, etc.)">
            <p className="text-[10px] text-zinc-500">Agregá stickers superpuestos para llamar la atención. Click para añadir, arrastrá los valores para posicionar.</p>
            <div className="flex flex-wrap gap-1 mb-2">
              {['⚡','✨','💥','🔥','💣','❗','🎉','💯','⭐','🎁','💸','🆕','🏆','👑','🚀','🎯'].map(em => (
                <button key={em} type="button"
                  onClick={() => upd('stickers', [...(layout.stickers||[]), { emoji: em, left: 50, top: 35, size: 64, rotate: 0 }])}
                  className="px-2 py-1 text-2xl bg-white border border-zinc-200 rounded hover:bg-zinc-50"
                  title={`Agregar ${em}`}>{em}</button>
              ))}
            </div>
            {(layout.stickers || []).map((s, idx) => (
              <div key={idx} className="border border-zinc-200 rounded p-2 space-y-1 bg-zinc-50">
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{s.emoji}</span>
                  <button type="button" onClick={() => upd('stickers', layout.stickers.filter((_, i) => i !== idx))}
                    className="text-xs text-red-500 hover:underline">Quitar</button>
                </div>
                <Row label="Left (%)" v={s.left} onChange={v => {
                  const arr = [...layout.stickers]; arr[idx] = { ...arr[idx], left: v }; upd('stickers', arr);
                }} min={0} max={100} step={1} />
                <Row label="Top (%)" v={s.top} onChange={v => {
                  const arr = [...layout.stickers]; arr[idx] = { ...arr[idx], top: v }; upd('stickers', arr);
                }} min={0} max={100} step={1} />
                <Row label="Tamaño (px)" v={s.size} onChange={v => {
                  const arr = [...layout.stickers]; arr[idx] = { ...arr[idx], size: v }; upd('stickers', arr);
                }} min={20} max={200} step={2} />
                <Row label="Rotación (°)" v={s.rotate || 0} onChange={v => {
                  const arr = [...layout.stickers]; arr[idx] = { ...arr[idx], rotate: v }; upd('stickers', arr);
                }} min={-180} max={180} step={5} />
              </div>
            ))}
          </Card>

          <Card title="📐 Formato de descarga">
            <p className="text-[10px] text-zinc-500">El banner se descarga siempre en formato <b>Historia 9:16 (1080×1920)</b>. Ideal para Stories de Instagram y estados de WhatsApp. El preview del editor refleja este mismo formato.</p>
            <FormatBtn label="Historia 1080×1920" sub="Stories Instagram / Estados WhatsApp 9:16" fmt="ig-story" productId={product?.id} selected={true} onSelect={() => { setSelectedFmt('ig-story'); setLayout(p => ({ ...p, format: 'ig-story' })); }} />
          </Card>

          <Card title="Precio por cantidad (badge tier)">
            <Check label="Mostrar badge 'LLEVANDO X u → $Y'" v={layout.showTierBadge !== false} onChange={(v) => upd('showTierBadge', v)} testid="bl-tier-on" />
            {layout.showTierBadge !== false && (
              <>
                <div className="flex items-start gap-2">
                  <label className="text-xs text-zinc-700 w-32 shrink-0 pt-1">Texto (plantilla)</label>
                  <input type="text" value={layout.tierBadgeTemplate ?? 'LLEVANDO {qty}u → {price}'}
                    onChange={(e) => upd('tierBadgeTemplate', e.target.value)}
                    className="flex-1 px-2 py-1 border border-zinc-200 rounded text-xs"
                    placeholder="LLEVANDO {qty}u → {price}"
                    data-testid="bl-tier-tpl" />
                </div>
                <p className="text-[10px] text-zinc-400 ml-32">Usá <code>{'{qty}'}</code> y <code>{'{price}'}</code> como variables. Ej: {'\u2018'}12u = $2.500{'\u2019'} o {'\u2018'}Mejor precio x{'{qty}'}u{'\u2019'}.</p>
                <Row label="Tamaño texto (px)" v={layout.tierBadgePx ?? 10} onChange={(v) => upd('tierBadgePx', v)} min={6} max={40} step={1} testid="bl-tier-px" />
                <Row label="Padding horiz. (px)" v={layout.tierBadgePadX ?? 8} onChange={(v) => upd('tierBadgePadX', v)} min={0} max={40} step={1} testid="bl-tier-padx" />
                <Row label="Padding vert. (px)" v={layout.tierBadgePadY ?? 2} onChange={(v) => upd('tierBadgePadY', v)} min={0} max={30} step={1} testid="bl-tier-pady" />
                <Row label="Radio borde (px)" v={layout.tierBadgeRadius ?? 999} onChange={(v) => upd('tierBadgeRadius', v)} min={0} max={999} step={1} testid="bl-tier-radius" />
                <Row label="Borde grosor (px)" v={layout.tierBadgeBorderPx ?? 0} onChange={(v) => upd('tierBadgeBorderPx', v)} min={0} max={10} step={1} testid="bl-tier-bw" />
                <div className="border-t border-zinc-200 my-2" />
                <ColorRow label="Color fondo" v={layout.tierBadgeBg ?? ''} onChange={(v) => upd('tierBadgeBg', v)} testid="bl-tier-bg" />
                <p className="text-[10px] text-zinc-400">Dejalo vacío para usar el gradiente naranja→rojo por defecto. Podés poner un color plano ej. <code>#000000</code> o un gradient css.</p>
                <input value={layout.tierBadgeBg ?? ''} onChange={(e) => upd('tierBadgeBg', e.target.value)}
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded text-xs font-mono"
                  placeholder="linear-gradient(90deg,#FB923C,#EF4444)" data-testid="bl-tier-bg-raw" />
                <ColorRow label="Color texto" v={layout.tierBadgeTextColor ?? '#ffffff'} onChange={(v) => upd('tierBadgeTextColor', v)} testid="bl-tier-color" />
                <ColorRow label="Color borde" v={layout.tierBadgeBorderColor ?? '#000000'} onChange={(v) => upd('tierBadgeBorderColor', v)} testid="bl-tier-border" />
                <Check label="Texto en negrita" v={layout.tierBadgeBold !== false} onChange={(v) => upd('tierBadgeBold', v)} testid="bl-tier-bold" />
                <Check label="Texto en MAYÚSCULAS" v={layout.tierBadgeUppercase !== false} onChange={(v) => upd('tierBadgeUppercase', v)} testid="bl-tier-upper" />
              </>
            )}
          </Card>

          <Card title="Precios por forma de pago">
            <Check label="Mostrar pills de forma de pago" v={layout.paymentChipsEnabled !== false} onChange={(v) => upd('paymentChipsEnabled', v)} testid="bl-pm-on" />
            {layout.paymentChipsEnabled !== false && (
              <>
                <Row label="Tamaño texto (px)" v={layout.paymentChipPx ?? 22} onChange={(v) => upd('paymentChipPx', v)} min={10} max={48} step={1} testid="bl-pm-px" />
                <Row label="Padding horiz. (px)" v={layout.paymentChipPadX ?? 10} onChange={(v) => upd('paymentChipPadX', v)} min={0} max={40} step={1} testid="bl-pm-padx" />
                <Row label="Padding vert. (px)" v={layout.paymentChipPadY ?? 4} onChange={(v) => upd('paymentChipPadY', v)} min={0} max={30} step={1} testid="bl-pm-pady" />
                <Row label="Radio borde (px)" v={layout.paymentChipRadius ?? 999} onChange={(v) => upd('paymentChipRadius', v)} min={0} max={999} step={1} testid="bl-pm-radius" />
                <Row label="Borde grosor (px)" v={layout.paymentChipBorderPx ?? 2} onChange={(v) => upd('paymentChipBorderPx', v)} min={0} max={10} step={1} testid="bl-pm-bw" />
                <Row label="Separación vert. (px)" v={layout.paymentChipGap ?? 6} onChange={(v) => upd('paymentChipGap', v)} min={0} max={30} step={1} testid="bl-pm-gap" />
                <div className="border-t border-zinc-200 my-2" />
                <ColorRow label="Fondo pill" v={layout.paymentChipBg ?? '#FFFFFFEE'} onChange={(v) => upd('paymentChipBg', v)} testid="bl-pm-bg" />
                <p className="text-[10px] text-zinc-400">Acepta rgba: ej. <code>rgba(255,255,255,0.94)</code></p>
                <input value={layout.paymentChipBg ?? ''} onChange={(e) => upd('paymentChipBg', e.target.value)}
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded text-xs font-mono" placeholder="rgba(255,255,255,0.94)" data-testid="bl-pm-bg-raw" />
                <div className="border-t border-zinc-200 my-2" />
                <p className="text-xs font-bold text-emerald-700 mt-1">Descuento (–%)</p>
                <ColorRow label="Color texto" v={layout.paymentChipDiscountColor ?? '#047857'} onChange={(v) => upd('paymentChipDiscountColor', v)} testid="bl-pm-disc-color" />
                <ColorRow label="Color borde" v={layout.paymentChipDiscountBorder ?? '#10B981'} onChange={(v) => upd('paymentChipDiscountBorder', v)} testid="bl-pm-disc-border" />
                <div className="border-t border-zinc-200 my-2" />
                <p className="text-xs font-bold text-rose-700">Recargo (+%)</p>
                <ColorRow label="Color texto" v={layout.paymentChipSurchargeColor ?? '#9F1239'} onChange={(v) => upd('paymentChipSurchargeColor', v)} testid="bl-pm-surch-color" />
                <ColorRow label="Color borde" v={layout.paymentChipSurchargeBorder ?? '#F43F5E'} onChange={(v) => upd('paymentChipSurchargeBorder', v)} testid="bl-pm-surch-border" />
                <Check label="Texto en negrita" v={layout.paymentChipBold !== false} onChange={(v) => upd('paymentChipBold', v)} testid="bl-pm-bold" />
              </>
            )}
          </Card>

          <Card title="Borde decorativo">
            <Row label="Ancho borde (px)" v={layout.borderWidth} onChange={(v) => upd('borderWidth', v)} min={0} max={20} step={1} />
            {layout.borderWidth > 0 && (
              <ColorRow label="Color borde" v={layout.borderColor} onChange={(v) => upd('borderColor', v)} testid="bl-border" />
            )}
          </Card>

          <Card title="URL del catálogo">
            <Check label="Mostrar URL en el banner" v={layout.showCatalogUrl} onChange={(v) => upd('showCatalogUrl', v)} testid="bl-showcat" />
            {layout.showCatalogUrl && (
              <>
                <input value={layout.catalogUrlText} onChange={(e) => upd('catalogUrlText', e.target.value)}
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded text-sm font-mono"
                  placeholder="bebida-bacana.com" data-testid="bl-caturl" />
                <Row label="Tamaño (px)" v={layout.catalogUrlPx || 12} onChange={(v) => upd('catalogUrlPx', v)} min={8} max={80} step={1} testid="bl-caturl-size" />
              </>
            )}
          </Card>

          <Card title="🎯 Precios por objetivo de carrito (tier list)">
            <p className="text-[10px] text-zinc-500">Muestra en el banner los precios bonificados por cada nivel de compra total (definidos en Admin → Tienda → Descuentos por objetivo).</p>
            <Check label="Mostrar tabla de tiers en el banner" v={layout.showTierList !== false} onChange={(v) => upd('showTierList', v)} testid="bl-tierlist-on" />
            {layout.showTierList !== false && (
              <>
                <input value={layout.tierListTitle ?? 'Precios por objetivo'} onChange={(e) => upd('tierListTitle', e.target.value)}
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded text-sm"
                  placeholder="Título (ej: Precios por objetivo)" data-testid="bl-tierlist-title" />
                <Row label="Tamaño (px)" v={layout.tierListPx ?? 18} onChange={(v) => upd('tierListPx', v)} min={10} max={48} step={1} testid="bl-tierlist-px" />
                <Row label="Left (%)" v={(layout.tierList && layout.tierList.left) ?? 4} onChange={(v) => upd('tierList', { ...(layout.tierList || {}), left: v })} min={0} max={80} step={0.5} testid="bl-tierlist-left" />
                <Row label="Bottom (%)" v={(layout.tierList && layout.tierList.bottom) ?? 12} onChange={(v) => upd('tierList', { ...(layout.tierList || {}), bottom: v })} min={0} max={90} step={0.5} testid="bl-tierlist-bottom" />
                <Row label="Ancho (%)" v={(layout.tierList && layout.tierList.width) ?? 92} onChange={(v) => upd('tierList', { ...(layout.tierList || {}), width: v })} min={20} max={100} step={1} testid="bl-tierlist-width" />
                <ColorRow label="Color texto" v={layout.tierListColor ?? '#0F172A'} onChange={(v) => upd('tierListColor', v)} testid="bl-tierlist-color" />
                <ColorRow label="Color acento" v={layout.tierListAccentColor ?? '#EC4899'} onChange={(v) => upd('tierListAccentColor', v)} testid="bl-tierlist-accent" />
                <ColorRow label="Fondo" v={layout.tierListBgColor ?? '#FFFFFF'} onChange={(v) => upd('tierListBgColor', v)} testid="bl-tierlist-bg" />
                <p className="text-[10px] text-zinc-400">Acepta rgba/hex. Dejalo opaco (#FFFFFF) si no querés que los badges se transparenten detrás.</p>
                <input value={layout.tierListBgColor ?? ''} onChange={(e) => upd('tierListBgColor', e.target.value)}
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded text-xs font-mono"
                  placeholder="#FFFFFF o rgba(255,255,255,0.85)" data-testid="bl-tierlist-bg-raw" />
                <Check label="Negrita" v={layout.tierListBold !== false} onChange={(v) => upd('tierListBold', v)} testid="bl-tierlist-bold" />
              </>
            )}
          </Card>

          <Card title="Pie de página">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={layout.showFooter} onChange={(e) => upd('showFooter', e.target.checked)} data-testid="bl-footer-on" />
              <span>Mostrar pie con teléfono WhatsApp</span>
            </label>
            {layout.showFooter && (
              <>
                <input value={layout.footerText} onChange={(e) => upd('footerText', e.target.value)}
                  className="w-full mt-2 px-2 py-1.5 border border-zinc-200 rounded text-sm"
                  placeholder="📲 Pedilo por WhatsApp" data-testid="bl-footer-text" />
                <ColorRow label="Fondo pie" v={layout.footerBgColor} onChange={(v) => upd('footerBgColor', v)} testid="bl-footerbg" />
                <ColorRow label="Texto pie" v={layout.footerTextColor} onChange={(v) => upd('footerTextColor', v)} testid="bl-footertxt" />
              </>
            )}
          </Card>

          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={saving}
              className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="bl-save">
              <Save size={14} /> {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
            <button onClick={resetDefaults}
              className="px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg text-sm font-bold flex items-center gap-2"
              data-testid="bl-reset">
              <RotateCcw size={14} /> Defaults
            </button>
          </div>
          {savedAt && <p className="text-xs text-emerald-700 font-bold">✓ Guardado.</p>}
        </div>

        {/* Preview en vivo */}
        <div className="bg-zinc-900 rounded-2xl p-5 sticky top-3 self-start" data-testid="bl-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <span className="text-white text-xs font-bold uppercase flex items-center gap-1"><Eye size={12} /> Vista previa {saving ? '· guardando...' : savedAt ? '· ✓ guardado' : ''}</span>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 bg-zinc-800 rounded px-2 py-1">
                <span className="text-[10px] text-zinc-400">Zoom</span>
                <input type="range" min="50" max="200" step="10" value={previewZoom}
                  onChange={(e) => setPreviewZoom(Number(e.target.value))}
                  className="w-20 accent-emerald-500"
                  data-testid="bl-preview-zoom" />
                <span className="text-[10px] font-bold text-white w-9 text-right">{previewZoom}%</span>
                <button onClick={() => setPreviewZoom(100)}
                  className="text-[9px] text-zinc-400 hover:text-white ml-1"
                  title="Resetear zoom">↺</button>
              </div>
              <button onClick={() => setEditMode(!editMode)}
                className={`px-2 py-1 text-xs rounded font-bold flex items-center gap-1 ${editMode ? 'bg-emerald-500 text-white' : 'bg-zinc-700 text-zinc-300'}`}
                title="Activar para mover/redimensionar con el mouse"
                data-testid="bl-edit-mode">
                <Move size={12} /> {editMode ? 'Drag ON' : 'Drag OFF'}
              </button>
              <select value={previewId} onChange={(e) => setPreviewId(e.target.value)}
                className="px-2 py-1 bg-zinc-800 text-white text-xs rounded border border-zinc-700"
                data-testid="bl-preview-product">
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {editMode && (
            <p className="text-[10px] text-emerald-400 mb-2">🖱 Arrastrá los bloques azules para mover. Tirá de las esquinas para redimensionar. Todos los cambios se guardan automáticamente.</p>
          )}
          {product && (
            <div ref={previewRef} style={{ width: `${previewZoom}%`, maxWidth: '100%', margin: '0 auto', transition: 'width 0.15s ease' }}>
              <BannerPreview product={product} layout={layout} storeSettings={storeSettings} fmt={selectedFmt}
                editMode={editMode} previewW={previewW} onLayoutChange={(patch) => setLayout(p => ({ ...p, ...patch }))} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function BannerPreview({ product, layout, storeSettings, fmt, editMode = false, previewW = 540, onLayoutChange }) {
  const price = product.priceNumeric || 0;
  const tiers = (product.quantityPrices || []).slice().sort((a, b) => a.minQty - b.minQty);
  const bestTier = tiers.length ? tiers[tiers.length - 1] : null;
  const bestSavingsPct = bestTier && price > 0 ? Math.round(((price - bestTier.priceNumeric) / price) * 100) : 0;

  // 🆕 Cart-target tiers (descuentos por objetivo de carrito, configurados en
  // Admin/Store). Se muestran en el banner como una "tabla de escalones":
  //   > $200k: $28.500 (-2%)
  //   > $400k: $27.900 (-3%)
  // Sólo aparecen tiers con pct > 0 y precio base > 0.
  const cartTargetTiers = ((storeSettings?.cartDiscountTiers) || [])
    .filter(t => Number(t?.threshold || 0) > 0 && Number(t?.pct || 0) > 0)
    .map(t => {
      const th = Number(t.threshold || 0);
      const pct = Number(t.pct || 0);
      const bonused = price > 0 ? Math.ceil(price * (1 - pct / 100) / 10) * 10 : 0;
      return { threshold: th, pct, bonused };
    })
    .sort((a, b) => a.threshold - b.threshold);

  // Formateador de moneda LOCAL. No usamos `fmt` como nombre porque el prop
  // `fmt` es el string de FORMATO del banner ('square', 'ig-story', etc.).
  const fmtCurrency = (n) => `$${(Number(n) || 0).toLocaleString('es-AR')}`;

  const getOriginalUrl = (url) => url ? encodeURI(url) : '';

  // Aspecto del canvas según formato (mismo mapeo que backend SIZES)
  const FMT_RATIO = {
    'square':        { w: 1,    h: 1 },
    'ig-story':      { w: 9,    h: 16 },
    'wa-square':     { w: 1,    h: 1 },
    'wa-horizontal': { w: 1200, h: 630 },
  };
  const rObj = FMT_RATIO[fmt] || { w: 1, h: 1 };
  const ratio = `${rObj.w} / ${rObj.h}`;
  // maxWidth dinámico: para story (vertical) usamos más ancho para aprovechar la pantalla del editor.
  const MAX_WIDTH = fmt === 'ig-story' ? 540 : fmt === 'wa-horizontal' ? 760 : 720;

  // Helpers para drag/resize: convertir px ↔ % usando ancho/alto REALES del canvas.
  // BUG previo: usábamos containerSize=previewW para ambos ejes → en formato 9:16 las
  // coordenadas verticales se quedaban "cortas" (no se podían arrastrar al fondo).
  const containerW = previewW;
  const containerH = previewW * (rObj.h / rObj.w);
  const pctX = (px) => (px / containerW) * 100;
  const pctY = (py) => (py / containerH) * 100;
  const toPxX = (p) => (p / 100) * containerW;
  const toPxY = (p) => (p / 100) * containerH;

  // ESCALA PROPORCIONAL para tipografía. Los `namePx`/`pricePx` están calibrados
  // para un canvas de referencia de 540px. Cuando Playwright captura el banner a
  // 1080×1080 (o cualquier otro tamaño), multiplicamos por la escala para que el
  // texto crezca proporcionalmente — así el PNG descargado se ve idéntico al preview.
  const REF_WIDTH = 540;
  const fontScale = containerW / REF_WIDTH;
  const sp = (px) => `${(px || 0) * fontScale}px`;

  // Wrap genérico para bloques editables. Si !editMode, devuelve un div absoluto sin Rnd.
  const Block = ({ keyPath, posPct, onChange, zIndex, children, lockAspect = false, rightAnchor = false, label }) => {
    if (!editMode || !onLayoutChange) {
      const styleAbs = rightAnchor
        ? { position: 'absolute', right: `${posPct.right ?? 0}%`, top: `${posPct.top}%`, width: `${posPct.width}%`, height: posPct.height != null ? `${posPct.height}%` : undefined, zIndex }
        : { position: 'absolute', left: `${posPct.left}%`, top: `${posPct.top}%`, width: `${posPct.width}%`, height: posPct.height != null ? `${posPct.height}%` : undefined, zIndex };
      return <div style={styleAbs}>{children}</div>;
    }
    // Convertir % → px (eje X usa el ancho del canvas, eje Y usa el alto real)
    const xPx = rightAnchor ? containerW - toPxX(posPct.right || 0) - toPxX(posPct.width) : toPxX(posPct.left || 0);
    const yPx = toPxY(posPct.top || 0);
    const wPx = toPxX(posPct.width || 30);
    const hPx = posPct.height != null ? toPxY(posPct.height) : undefined;
    return (
      <Rnd
        position={{ x: xPx, y: yPx }}
        size={{ width: wPx, height: hPx || 'auto' }}
        lockAspectRatio={lockAspect}
        onDragStop={(e, d) => {
          // Permitimos posiciones fuera de los límites (-50 a 150) para overflow creativo
          const patch = rightAnchor
            ? { ...posPct, right: Math.max(-50, Math.min(150, pctX(containerW - d.x - wPx))), top: Math.max(-50, Math.min(150, pctY(d.y))) }
            : { ...posPct, left: Math.max(-50, Math.min(150, pctX(d.x))), top: Math.max(-50, Math.min(150, pctY(d.y))) };
          onChange(patch);
        }}
        onResizeStop={(e, dir, ref, delta, pos) => {
          const newW = ref.offsetWidth, newH = ref.offsetHeight;
          const patch = rightAnchor
            ? { ...posPct, width: pctX(newW), height: posPct.height != null ? pctY(newH) : undefined, right: pctX(containerW - pos.x - newW), top: pctY(pos.y) }
            : { ...posPct, width: pctX(newW), height: posPct.height != null ? pctY(newH) : undefined, left: pctX(pos.x), top: pctY(pos.y) };
          onChange(patch);
        }}
        style={{ zIndex, outline: '1px dashed rgba(16,185,129,0.6)', outlineOffset: '-1px', cursor: 'move' }}
      >
        {label && (
          <div style={{ position: 'absolute', top: -16, left: 0, fontSize: 9, fontWeight: 700, color: '#10B981', background: '#fff', padding: '1px 4px', borderRadius: 3, lineHeight: 1, pointerEvents: 'none' }}>{label}</div>
        )}
        <div style={{ width: '100%', height: '100%' }}>{children}</div>
      </Rnd>
    );
  };

  // Bloques de texto separados (con fallback al "text" legacy)
  const tName = layout.textName || layout.text;
  const tPrice = layout.textPrice; // si null, renderea junto al nombre (legacy)
  const tQty = layout.textQty;

  const updLogo = (patch) => onLayoutChange?.({ logo: { ...layout.logo, ...patch } });
  const updImage = (patch) => onLayoutChange?.({ image: { ...layout.image, ...patch } });
  const updTextName = (patch) => onLayoutChange?.({ textName: { ...(layout.textName || layout.text), ...patch } });
  const updTextPrice = (patch) => onLayoutChange?.({ textPrice: { ...(layout.textPrice || { top: 60, left: 5, width: 60, maxHeight: 20 }), ...patch } });
  const updTextQty = (patch) => onLayoutChange?.({ textQty: { ...(layout.textQty || { top: 85, left: 5, width: 60, maxHeight: 12 }), ...patch } });

  return (
    <div className="relative mx-auto"
      style={{
        width: '100%', maxWidth: MAX_WIDTH, aspectRatio: ratio,
        background: layout.bgColor || '#fff',
        border: layout.borderWidth > 0 ? `${layout.borderWidth}px solid ${layout.borderColor}` : undefined,
        borderRadius: '24px',
        overflow: editMode ? 'visible' : 'hidden',
        outline: editMode ? '2px solid rgba(16,185,129,0.5)' : undefined,
        outlineOffset: editMode ? '-2px' : undefined,
      }}>
      {layout.topText && (
        <div className="absolute" style={{ top: '0.5%', left: '50%', transform: 'translateX(-50%)', zIndex: 8 }}>
          <span style={{
            color: layout.topTextColor, fontSize: sp(layout.topTextPx),
            fontFamily: cssFontFamily(layout.fontFamily, 'Anton, "Barlow Condensed", sans-serif'),
            fontWeight: layout.topBold ? 900 : 400,
            fontStyle: layout.topItalic ? 'italic' : 'normal',
            textTransform: 'uppercase',
            letterSpacing: `${1 * fontScale}px`,
          }}>{layout.topText}</span>
        </div>
      )}

      {/* LOGO */}
      <Block posPct={{ top: layout.logo.top, left: layout.logo.left, width: layout.logo.width }}
        onChange={updLogo} zIndex={5} lockAspect label="LOGO">
        <img src="/bebida-bacana-logo.png" alt="logo"
          className="w-full h-auto block select-none pointer-events-none"
          style={{ transform: layout.logo.rotate ? `rotate(${layout.logo.rotate}deg)` : undefined }} />
      </Block>

      {/* IMAGEN producto */}
      <Block posPct={{ right: layout.image.right, top: layout.image.top, width: layout.image.width, height: layout.image.height }}
        onChange={updImage} zIndex={1} rightAnchor label="IMAGEN">
        <div style={{ width: '100%', height: '100%', transform: layout.image.rotate ? `rotate(${layout.image.rotate}deg)` : undefined }}>
          <img src={getOriginalUrl(product.imageUrl)} alt={product.name}
            className="w-full h-full object-contain pointer-events-none"
            onError={(e) => { e.target.src = 'https://placehold.co/600x600/ffffff/cbd5e1?text=' + encodeURIComponent(product.name); }} />
        </div>
      </Block>

      {/* STICKERS de venta superpuestos */}
      {(layout.stickers || []).map((s, idx) => (
        <div key={idx} className="absolute" style={{ left: `${s.left}%`, top: `${s.top}%`, fontSize: sp(s.size || 48), zIndex: 25, transform: s.rotate ? `rotate(${s.rotate}deg)` : undefined, lineHeight: 1, pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
          {s.emoji}
        </div>
      ))}

      {/* NOMBRE */}
      <Block posPct={{ top: tName.top, left: tName.left, width: tName.width, height: tName.maxHeight }}
        onChange={(p) => updTextName({ top: p.top, left: p.left, width: p.width, maxHeight: p.height ?? tName.maxHeight })}
        zIndex={20} label="NOMBRE">
        <p className="break-words"
          style={{
            fontFamily: cssFontFamily(layout.fontFamily, 'Anton, "Barlow Condensed", sans-serif'),
            fontWeight: layout.nameBold === false ? 400 : 900,
            fontStyle: layout.nameItalic ? 'italic' : 'normal',
            textTransform: layout.nameUppercase === false ? 'none' : 'uppercase',
            fontSize: sp(product.name.length > 36 ? Math.max(16, layout.namePx - 8) : product.name.length > 24 ? Math.max(18, layout.namePx - 4) : layout.namePx),
            color: layout.nameColor, letterSpacing: `${0.5 * fontScale}px`, lineHeight: layout.lineHeight,
            display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: layout.nameLines, overflow: 'hidden',
            margin: 0,
          }}>{product.name}</p>
      </Block>

      {/* PRECIO */}
      <Block posPct={tPrice || { top: (tName.top || 30) + 22, left: tName.left, width: tName.width, height: 15 }}
        onChange={(p) => updTextPrice({ top: p.top, left: p.left, width: p.width, maxHeight: p.height })}
        zIndex={20} label="PRECIO">
        <p className="tabular-nums"
          style={{
            fontFamily: cssFontFamily(layout.fontFamily, 'Anton, "Barlow Condensed", sans-serif'),
            fontWeight: layout.priceBold === false ? 400 : 900,
            fontStyle: layout.priceItalic ? 'italic' : 'normal',
            fontSize: sp(layout.pricePx), color: layout.priceColor,
            lineHeight: 1, margin: 0,
          }}>
          ${(price || 0).toLocaleString('es-AR')}
        </p>
      </Block>

      {/* CANTIDAD / TIERS / BADGES */}
      <Block posPct={tQty || { top: (tName.top || 30) + 40, left: tName.left, width: tName.width, height: 18 }}
        onChange={(p) => updTextQty({ top: p.top, left: p.left, width: p.width, maxHeight: p.height })}
        zIndex={20} label="CANTIDAD / BADGES">
        <p className="font-semibold text-zinc-700" style={{ margin: 0, fontSize: sp(12) }}>
          por unidad · pedido mín. x{product.packSize > 1 ? product.packSize : 1}
        </p>
        {/* Pills de forma de pago — preview en el editor */}
        {layout.paymentChipsEnabled !== false && (storeSettings?.paymentMethods || [])
          .filter(m => m && m.enabled !== false && Number(m.adjustPercent || 0) !== 0)
          .slice(0, 3)
          .map(m => {
            const adj = Number(m.adjustPercent || 0);
            // Base = SIEMPRE precio de lista (no el tier). El descuento por
            // forma de pago se muestra al lado del precio base, aunque el
            // producto también tenga descuento por cantidad. Así el cliente
            // ve ambas opciones (comprar menos cantidad + efectivo, o llegar
            // al tier — el tier suele ser el mejor).
            const base = (price || 0);
            const adjusted = Math.ceil(base * (1 + adj / 100));
            const isDiscount = adj < 0;
            const color = isDiscount ? (layout.paymentChipDiscountColor ?? '#047857') : (layout.paymentChipSurchargeColor ?? '#9F1239');
            const border = isDiscount ? (layout.paymentChipDiscountBorder ?? '#10B981') : (layout.paymentChipSurchargeBorder ?? '#F43F5E');
            const bw = layout.paymentChipBorderPx ?? 2;
            return (
              <div key={m.key} style={{ marginTop: sp(layout.paymentChipGap ?? 6) }}>
                <span className="tabular-nums leading-tight inline-block"
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: sp(layout.paymentChipPx ?? 22),
                    fontWeight: layout.paymentChipBold !== false ? 900 : 600,
                    color,
                    background: layout.paymentChipBg ?? 'rgba(255,255,255,0.94)',
                    padding: `${sp(layout.paymentChipPadY ?? 4)} ${sp(layout.paymentChipPadX ?? 10)}`,
                    borderRadius: `${layout.paymentChipRadius ?? 999}px`,
                    border: bw > 0 ? `${sp(bw)} solid ${border}` : 'none',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                  }}>
                  {m.label} <span style={{ opacity: 0.75 }}>({adj > 0 ? '+' : ''}{adj}%)</span> · ${adjusted.toLocaleString('es-AR')}
                </span>
              </div>
            );
          })}
        <div className="mt-1 flex flex-wrap" style={{ gap: sp(layout.tierBadgeGap ?? 4) }}>
          {layout.showTierBadge !== false && bestTier && (() => {
            const tbBg = layout.tierBadgeBg && layout.tierBadgeBg !== '' ? layout.tierBadgeBg : `linear-gradient(90deg,${layout.accentColor || '#FB923C'},#EF4444)`;
            const tbColor = layout.tierBadgeTextColor || '#ffffff';
            const tbPx = layout.tierBadgePx ?? 10;
            const tbPadX = layout.tierBadgePadX ?? 8;
            const tbPadY = layout.tierBadgePadY ?? 2;
            const tbBorderPx = layout.tierBadgeBorderPx ?? 0;
            const tbBorderColor = layout.tierBadgeBorderColor || 'transparent';
            const tbRadius = layout.tierBadgeRadius ?? 999;
            const tbBold = layout.tierBadgeBold !== false;
            const tbUppercase = layout.tierBadgeUppercase !== false;
            const tbFontFamily = cssFontFamily(layout.tierBadgeFontFamily || layout.fontFamily, 'Inter, system-ui, sans-serif');
            return (
              <span className="inline-block tabular-nums leading-tight" style={{
                background: tbBg,
                color: tbColor,
                fontSize: sp(tbPx),
                fontWeight: tbBold ? 900 : 600,
                textTransform: tbUppercase ? 'uppercase' : 'none',
                padding: `${sp(tbPadY)} ${sp(tbPadX)}`,
                borderRadius: `${tbRadius}px`,
                border: tbBorderPx > 0 ? `${sp(tbBorderPx)} solid ${tbBorderColor}` : 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                fontFamily: tbFontFamily,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {(layout.tierBadgeTemplate || 'LLEVANDO {qty}u → {price}')
                  .replace('{qty}', bestTier.minQty)
                  .replace('{price}', fmtCurrency(bestTier.priceNumeric))}
              </span>
            );
          })()}
          {layout.showDiscountBadge !== false && bestSavingsPct > 0 && (
            <span className="rounded-full font-black bg-yellow-300 text-red-600" style={{ fontSize: sp(10), padding: `${sp(2)} ${sp(8)}` }}>-{bestSavingsPct}%</span>
          )}
          {layout.showOfertaBadge !== false && product.oferta && !bestTier && (
            <span className="rounded-full font-black bg-emerald-400 text-emerald-900" style={{ fontSize: sp(10), padding: `${sp(2)} ${sp(8)}` }}>🔥 OFERTA</span>
          )}
          {layout.showDestacadoBadge !== false && product.destacado && !bestTier && (
            <span className="rounded-full font-black bg-amber-300 text-amber-900" style={{ fontSize: sp(10), padding: `${sp(2)} ${sp(8)}` }}>⭐ DESTACADO</span>
          )}
        </div>
        {layout.showCatalogUrl && (
          <p className="mt-1 font-mono font-bold text-zinc-700 bg-zinc-100 inline-block rounded" style={{ fontSize: sp(layout.catalogUrlPx || 12), padding: `${sp(2)} ${sp(6)}` }}>
            🌐 {layout.catalogUrlText}
          </p>
        )}
      </Block>

      {layout.showFooter && (
        <div className="absolute left-0 right-0 bottom-0 z-30" style={{ background: layout.footerBgColor || 'transparent', padding: `${sp(6)} ${sp(16)} ${sp(12)}` }}>
          <div className="flex items-center justify-between border-t-2 border-dashed border-zinc-300"
            style={{ color: layout.footerTextColor || '#404040', paddingTop: sp(8) }}>
            <span className="font-extrabold" style={{ fontSize: sp(12) }}>{layout.footerText}</span>
            <span className="font-black tabular-nums" style={{ fontSize: sp(13) }}>{storeSettings?.storePhone || '3571522400'}</span>
          </div>
        </div>
      )}

      {/* 🆕 Tabla de precios bonificados por objetivo de carrito. Editable
          desde el editor de banners (tamaño, colores, título y visibilidad).
          zIndex 35 (inline, no Tailwind arbitrary) para quedar POR ENCIMA
          del footer (30) y la fila de badges. */}
      {layout.showTierList !== false && cartTargetTiers.length > 0 && (
        <div
          className="absolute"
          style={{
            zIndex: 35,
            left: `${(layout.tierList && layout.tierList.left) ?? 4}%`,
            bottom: `${(layout.tierList && layout.tierList.bottom) ?? (layout.showFooter ? 18 : 6)}%`,
            width: `${(layout.tierList && layout.tierList.width) ?? 92}%`,
          }}
          data-testid="banner-tier-list"
        >
          <div
            style={{
              background: layout.tierListBgColor || '#FFFFFF',
              borderRadius: `${sp(10)}`,
              padding: `${sp(6)} ${sp(10)}`,
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              border: `${sp(1)} solid ${layout.tierListAccentColor || '#EC4899'}`,
            }}
          >
            {layout.tierListTitle && (
              <div
                style={{
                  fontFamily: cssFontFamily(layout.fontFamily, 'Inter, system-ui, sans-serif'),
                  fontWeight: 900,
                  fontSize: sp(Math.max(10, (layout.tierListPx || 18) - 4)),
                  color: layout.tierListAccentColor || '#EC4899',
                  textTransform: 'uppercase',
                  letterSpacing: `${0.4 * fontScale}px`,
                  lineHeight: 1.1,
                  marginBottom: sp(2),
                }}
              >
                {layout.tierListTitle}
              </div>
            )}
            <div style={{ display: 'grid', gap: sp(2) }}>
              {cartTargetTiers.map((t, idx) => (
                <div
                  key={idx}
                  className="tabular-nums"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontFamily: cssFontFamily(layout.fontFamily, 'Inter, system-ui, sans-serif'),
                    fontWeight: layout.tierListBold === false ? 600 : 900,
                    fontSize: sp(layout.tierListPx || 18),
                    color: layout.tierListColor || '#0F172A',
                    lineHeight: 1.1,
                  }}
                >
                  <span>{`> $${(t.threshold >= 1000 ? `${Math.round(t.threshold / 1000)}k` : t.threshold.toLocaleString('es-AR'))}`}</span>
                  <span style={{ color: layout.tierListAccentColor || '#EC4899' }}>
                    ${t.bonused.toLocaleString('es-AR')}
                    <span style={{ opacity: 0.65, fontSize: sp(Math.max(8, (layout.tierListPx || 18) - 6)), marginLeft: sp(3) }}>
                      (-{t.pct}%)
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2">
    <h3 className="text-[10px] font-bold uppercase text-zinc-500">{title}</h3>
    {children}
  </div>
);

const Row = ({ label, v, onChange, min, max, step, testid }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-zinc-700 w-32 shrink-0">{label}</label>
    <input type="range" min={min} max={max} step={step} value={v}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="flex-1" data-testid={testid} />
    <input type="number" min={min} max={max} step={step} value={v}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="w-16 px-1 py-0.5 border border-zinc-200 rounded text-xs text-right tabular-nums" />
  </div>
);

const ColorRow = ({ label, v, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-zinc-700 w-32 shrink-0">{label}</label>
    <input type="color" value={v || '#000000'} onChange={(e) => onChange(e.target.value)}
      className="h-8 w-10 border border-zinc-200 rounded cursor-pointer" data-testid={testid} />
    <input type="text" value={v} onChange={(e) => onChange(e.target.value)}
      className="flex-1 px-2 py-0.5 border border-zinc-200 rounded text-xs font-mono" />
  </div>
);

const Check = ({ label, v, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs cursor-pointer">
    <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    <span>{label}</span>
  </label>
);


const StyleRow = ({ label, b, i, onBold, onItalic, testid }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-zinc-700 w-32 shrink-0">{label}</label>
    <button onClick={() => onBold(!b)}
      className={`px-2 py-1 rounded text-xs font-bold border ${b ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100'}`}
      data-testid={`${testid}-bold`} title="Negrita">
      <Bold size={12} />
    </button>
    <button onClick={() => onItalic(!i)}
      className={`px-2 py-1 rounded text-xs italic border ${i ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100'}`}
      data-testid={`${testid}-italic`} title="Cursiva">
      <Italic size={12} />
    </button>
  </div>
);


const FormatBtn = ({ label, sub, fmt, productId, selected = false, onSelect }) => {
  if (!productId) return null;
  // `t=Date.now()` invalida el cache del backend para siempre traer la última versión
  // del layout guardado (evita descargar una imagen vieja tras editar el diseño).
  const url = `${API}/broadcast-assets/banner/${encodeURIComponent(productId)}.png?format=${fmt}&engine=playwright&t=${Date.now()}`;
  const baseCls = selected
    ? 'block p-2 bg-emerald-50 border-2 border-emerald-500 rounded text-center'
    : 'block p-2 bg-white border border-zinc-200 rounded hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-center cursor-pointer';
  return (
    <div className={baseCls} data-testid={`bl-fmt-${fmt}`} onClick={onSelect}>
      <div className="text-xs font-bold text-zinc-900">{label}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
      <a href={url} target="_blank" rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-block mt-1 px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded"
        download>
        Descargar
      </a>
    </div>
  );
};
